// import "./StockRecord.scss";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  DatePicker,
  Grid,
  Modal,
  Row,
  Input,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from "antd";
import { ColumnsType } from "antd/es/table";
import { FilterDropdownProps, SorterResult } from "antd/es/table/interface";
import {
  DownloadOutlined,
  PieChartOutlined,
  SearchOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { Stock, UserLeaveBalance } from "../../utils/types";
import {
  fetchData,
  fixedTableScrollY,
  hasRoleAccess,
  Notify,
  rowsPerPage,
} from "../../utils/constants";
import { ApexOptions } from "apexcharts";
import ReactApexChart from "react-apexcharts";
import { useUser } from "../../context/UserContext";
import { theme } from "antd";
import { apiClient } from "../../utils/httpClient";
import dayjs, { Dayjs } from "dayjs";
import { toUtcDateString } from "../../utils/date";
import {
  activeColumn,
  DEFAULT_EMPLOYEE_STATUS_FILTER,
  EmployeeStatusFilter,
  EmployeeStatusFilterValue,
  employeeStatusParam,
  matchesEmployeeStatus,
} from "../Filters/EmployeeStatusFilter";

// The 5 new-leave-type "days taken" columns, toggleable from the UI and
// hidden by default (see visibleExtraColumns in StockRecord).
const EXTRA_COLUMN_OPTIONS = [
  { label: "Marriage days", value: "marriageDays" },
  { label: "Child birth days", value: "childBirthDays" },
  { label: "Death of relative days", value: "deathOfRelativeDays" },
  { label: "Blood donation days", value: "bloodDonationDays" },
  { label: "Unpaid leave days", value: "unpaidLeaveDays" },
];
const EXTRA_COLUMN_KEYS = new Set(
  EXTRA_COLUMN_OPTIONS.map((option) => option.value),
);

const DonutChart = React.memo(
  ({
    userData,
    title,
  }: {
    userData: UserLeaveBalance | null;
    title: string;
  }) => {
    const { token } = theme.useToken();

    const data = React.useMemo(() => {
      return (
        userData || {
          currentYearDays: 0,
          previousYearDays: 0,
          usedCurrentYearDays: 0,
          usedPreviousYearDays: 0,
          availableDays: 0,
          employeeName: "",
        }
      );
    }, [userData]);

    const chartOptions: ApexOptions = React.useMemo(
      () => ({
        chart: { type: "donut", background: "transparent" },
        labels: [
          "Current Year Days Available",
          "Previous Year Days",
          "Used Current Year Days",
          "Used Previous Year Days",
        ],
        dataLabels: {
          enabled: true,
          style: { fontSize: "10px", fontWeight: "bold" },
          dropShadow: { enabled: false },
          offsetX: 0,
        },
        plotOptions: {
          pie: {
            donut: {
              size: "70%",
              labels: {
                show: true,
                name: { show: true },
                value: { show: true },
                total: {
                  show: true,
                  label: "Remaining days",
                  formatter: () => data.availableDays.toString(),
                },
              },
            },
          },
        },
        legend: { position: "bottom" },
        theme: { mode: token.colorBgBase === "#000" ? "dark" : "light" },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: { width: 300 },
              legend: { position: "bottom" },
            },
          },
        ],
      }),
      [data, token],
    );

    const chartSeries = React.useMemo(
      () => [
        data.currentYearDays - data.usedCurrentYearDays,
        data.previousYearDays - data.usedPreviousYearDays,
        data.usedCurrentYearDays,
        data.usedPreviousYearDays,
      ],
      [data],
    );

    if (!userData) return null;

    return (
      <div className="flex items-center justify-center flex-col">
        <h3>{title}</h3>
        <ReactApexChart
          options={chartOptions}
          series={chartSeries}
          type="donut"
          width={380}
        />
      </div>
    );
  },
);
DonutChart.displayName = "DonutChart";

const StockRecord = () => {
  const { authUser, roles } = useUser();
  const screens = Grid.useBreakpoint?.() || {};
  const isMobile = !screens.lg;

  const [stocks, setStocks] = useState<Stock[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [selectedUserData, setSelectedUserData] =
    useState<UserLeaveBalance | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(rowsPerPage);
  const [tableFilters, setTableFilters] = useState<
    Record<string, (string | number)[] | null>
  >({});
  const [tableSorter, setTableSorter] = useState<{
    field?: string;
    order?: "ascend" | "descend";
  }>({ field: "employeeName", order: "ascend" });
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const effectiveDate = selectedDate ?? dayjs();
  const selectedYear = effectiveDate.year();
  const effectiveDateKey = toUtcDateString(effectiveDate.toDate());
  const isHR = roles.some((role) => role.replace(/^ROLE_/, "") === "HR");
  const canRunRollover = hasRoleAccess(roles, ["ADMIN", "HR"]);
  const [rolloverModalOpen, setRolloverModalOpen] = useState(false);
  const [rolloverLoading, setRolloverLoading] = useState(false);
  // The 5 new-leave-type "days taken" columns are opt-in and hidden by
  // default - the table already has a lot of columns, and most viewers only
  // care about annual leave most of the time.
  const [visibleExtraColumns, setVisibleExtraColumns] = useState<string[]>(
    [],
  );
  const [employeeStatusFilter, setEmployeeStatusFilter] =
    useState<EmployeeStatusFilterValue>(DEFAULT_EMPLOYEE_STATUS_FILTER);
  const statusFilteredStocks = useMemo(
    () =>
      stocks.filter((item) =>
        matchesEmployeeStatus(item.employeeStatus, employeeStatusFilter),
      ),
    [stocks, employeeStatusFilter],
  );
  const filteredSortedStocks = useMemo(() => {
    let data = [...statusFilteredStocks];

    const employeeFilters = tableFilters.employeeName as string[] | null;
    if (employeeFilters && employeeFilters.length) {
      const searchTerm = String(employeeFilters[0] || "").toLowerCase();
      data = data.filter((item) =>
        String(item.employeeName || "").toLowerCase().includes(searchTerm),
      );
    }

    const departmentFilters = tableFilters.departmentName as string[] | null;
    if (departmentFilters && departmentFilters.length) {
      data = data.filter((item) =>
        departmentFilters.includes(String(item.departmentName || "-")),
      );
    }

    const availableFilters = tableFilters.availableDays as string[] | null;
    if (availableFilters && availableFilters.length) {
      data = data.filter((item) => {
        return availableFilters.some((filter) => {
          if (filter === "0") return item.availableDays === 0;
          if (filter === "1-5")
            return item.availableDays >= 1 && item.availableDays <= 5;
          if (filter === "6-10")
            return item.availableDays >= 6 && item.availableDays <= 10;
          if (filter === "10+") return item.availableDays > 10;
          return true;
        });
      });
    }

    if (tableSorter.field && tableSorter.order) {
      const { field, order } = tableSorter;
      data.sort((a, b) => {
        const aVal = (a as Record<string, unknown>)[field];
        const bVal = (b as Record<string, unknown>)[field];

        if (typeof aVal === "number" && typeof bVal === "number") {
          return order === "ascend" ? aVal - bVal : bVal - aVal;
        }
        const aStr = String(aVal ?? "");
        const bStr = String(bVal ?? "");
        return order === "ascend"
          ? aStr.localeCompare(bStr)
          : bStr.localeCompare(aStr);
      });
    }

    return data;
  }, [statusFilteredStocks, tableFilters, tableSorter]);

  const teamTotals = useMemo(() => {
    const data = statusFilteredStocks;
    if (!data || data.length === 0) return null;
    return {
      employeeName: "Team (aggregate)",
      currentYearDays: data.reduce((s, r) => s + (r.currentYearDays ?? 0), 0),
      previousYearDays: data.reduce(
        (s, r) => s + (r.previousYearDays ?? 0),
        0,
      ),
      usedCurrentYearDays: data.reduce(
        (s, r) => s + ((r.currentYearDays ?? 0) - (r.availableDays ?? 0)),
        0,
      ),
      usedPreviousYearDays: 0, // fill from your data if available
      availableDays: data.reduce((s, r) => s + (r.availableDays ?? 0), 0),
    } as UserLeaveBalance;
  }, [statusFilteredStocks]);

  const fetchStocks = useCallback(async () => {
    setLoading(true);
    try {
      await fetchData(
        `${process.env.REACT_APP_API_URL}/leave-balance/getLeaveBalancesForTeam/${selectedYear}?date=${effectiveDateKey}`,
        setStocks,
      );
    } catch (error) {
      console.error("Failed to fetch stock data:", error);
      Notify("error", "Could not load stock data. Please try again.");
    } finally {
      setLoading(false);
    }
    setSelectedUser(null);
    setSelectedUserData(null);
  }, [selectedYear, effectiveDateKey]);

  const fetchLeaveBalanceUserYear = useCallback(async () => {
    try {
      await fetchData(
        `${process.env.REACT_APP_API_URL}/leave-balance/details/${selectedUser}/${selectedYear}`,
        setSelectedUserData,
      );
    } catch (error) {
      console.error("Failed to fetch user chart data:", error);
      Notify("error", "Could not load user chart data. Please try again.");
    }
  }, [selectedUser, selectedYear]);

  useEffect(() => {
    if (!authUser) return;
    fetchStocks();
  }, [selectedYear, effectiveDateKey, authUser, fetchStocks]);

  const handleReportDownload = async () => {
    if (!authUser?.email) return;

    try {
      const response = await apiClient.post(
        `${process.env.REACT_APP_API_URL}/manager-leave-balance/export-excel/filtered`,
        {
          date: toUtcDateString(effectiveDate.toDate()),
          employeeStatus: employeeStatusParam(employeeStatusFilter),
        },
        {
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/octet-stream",
      });

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;

      const contentDisposition = response.headers["content-disposition"];
      const filenameMatch = contentDisposition?.match(
        /filename="?([^";\n]+)"?/,
      );
      const filename = filenameMatch ? filenameMatch[1] : "report.xlsx";

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Download error:", err);
      Notify("error", "Could not download report");
    }
  };

  const handleRunRollover = async (overwriteExisting: boolean) => {
    setRolloverLoading(true);
    try {
      const response = await apiClient.post(
        `${process.env.REACT_APP_API_URL}/annual-leave-rollover/manual?overwriteExisting=${overwriteExisting}`,
      );
      const summary = response.data as {
        targetYear: number;
        previousYear: number;
        totalActiveEmployees: number;
        created: number;
        overwritten: number;
        finalizedProvisional: number;
        skippedExisting: number;
        skippedNoPreviousBalance: number;
        skippedNoEntitlementEmployees: string[];
      };
      const skippedNoEntitlementCount =
        summary.skippedNoEntitlementEmployees?.length ?? 0;
      Notify(
        "success",
        `Rollover for ${summary.targetYear} done: ${summary.created} created, ` +
          `${summary.overwritten} overwritten, ${summary.finalizedProvisional} early bookings finalized, ` +
          `${summary.skippedExisting} already existed (skipped), ` +
          `${summary.skippedNoPreviousBalance} had no ${summary.previousYear} balance (skipped), ` +
          `${skippedNoEntitlementCount} skipped (no Entitlement configured).`,
      );
      if (skippedNoEntitlementCount > 0) {
        Notify(
          "error",
          `Missing Entitlement for: ${summary.skippedNoEntitlementEmployees.join(", ")}`,
        );
      }
      setRolloverModalOpen(false);
      fetchStocks();
    } catch (err) {
      console.error("Rollover error:", err);
      Notify("error", "Could not run the annual leave rollover.");
    } finally {
      setRolloverLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedUser) return;
    fetchLeaveBalanceUserYear();
  }, [fetchLeaveBalanceUserYear, selectedUser]);

  useEffect(() => {
    setCurrentPage(1);
  }, [stocks, selectedYear, employeeStatusFilter]);

  const columns: ColumnsType<Stock> = [
    {
      title: "Year",
      dataIndex: "year",
      key: "year",
      width: 90,
      sorter: (a, b) => a.year - b.year,
      sortOrder: tableSorter.field === "year" ? tableSorter.order : undefined,
    },
    {
      title: "Employee name",
      dataIndex: "employeeName",
      key: "employeeName",
      filteredValue: tableFilters.employeeName || null,
      filterDropdown: ({
        selectedKeys,
        setSelectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search employee"
            value={selectedKeys[0] as string}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              size="small"
              onClick={() => confirm()}
            >
              Search
            </Button>
            <Button
              size="small"
              onClick={() => {
                clearFilters?.();
                confirm();
              }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
      onFilter: (value, record) =>
        record.employeeName
          ?.toLowerCase()
          .includes(String(value).toLowerCase()) || false,
      sorter: (a, b) => a.employeeName.localeCompare(b.employeeName),
      sortOrder:
        tableSorter.field === "employeeName" ? tableSorter.order : undefined,
    },
    {
      ...activeColumn<Stock>(),
      sortOrder:
        tableSorter.field === "employeeStatus" ? tableSorter.order : undefined,
    },
    ...(isHR
      ? [
          {
            title: "Department",
            dataIndex: "departmentName",
            key: "departmentName",
            filters: Array.from(
              new Set(
                stocks
                  .map((item) => item.departmentName || "-")
                  .filter((item) => !!item),
              ),
            ).map((name) => ({ text: String(name), value: String(name) })),
            filteredValue: tableFilters.departmentName || null,
            onFilter: (value: React.Key | boolean, record: Stock) =>
              (record.departmentName || "-") === value,
            sorter: (a: Stock, b: Stock) =>
              String(a.departmentName || "").localeCompare(
                String(b.departmentName || ""),
              ),
            sortOrder:
              tableSorter.field === "departmentName"
                ? tableSorter.order
                : undefined,
          },
        ]
      : []),
    {
      title: "Current year annual leave balance",
      dataIndex: "currentYearDays",
      key: "currentYearDays",
      width: 160,
      sorter: (a, b) => a.currentYearDays - b.currentYearDays,
      sortOrder:
        tableSorter.field === "currentYearDays" ? tableSorter.order : undefined,
    },
    {
      title: "Previous year annual leave balance",
      dataIndex: "previousYearDays",
      key: "previousYearDays",
      width: 170,
      sorter: (a, b) => a.previousYearDays - b.previousYearDays,
      sortOrder:
        tableSorter.field === "previousYearDays"
          ? tableSorter.order
          : undefined,
    },
    {
      title: "Total available annual leave",
      key: "totalAvailableDays",
      width: 170,
      sorter: (a, b) =>
        a.currentYearDays + a.previousYearDays -
        (b.currentYearDays + b.previousYearDays),
      sortOrder:
        tableSorter.field === "totalAvailableDays"
          ? tableSorter.order
          : undefined,
      render: (_: unknown, item: Stock) =>
        item.currentYearDays + item.previousYearDays,
    },
    {
      title: "Annual leave taken days",
      dataIndex: "takenDays",
      key: "takenDays",
      width: 120,
      sorter: (a, b) =>
        (a.takenDays ??
          (a.usedCurrentYearDays || 0) + (a.usedPreviousYearDays || 0)) -
        (b.takenDays ??
          (b.usedCurrentYearDays || 0) + (b.usedPreviousYearDays || 0)),
      sortOrder:
        tableSorter.field === "takenDays" ? tableSorter.order : undefined,
      render: (_: unknown, item: Stock) =>
        item.takenDays ??
        (item.usedCurrentYearDays || 0) + (item.usedPreviousYearDays || 0),
    },
    {
      title: "Remaining annual leave days",
      dataIndex: "availableDays",
      key: "availableDays",
      width: 170,
      filters: [
        { text: "0", value: "0" },
        { text: "1-5", value: "1-5" },
        { text: "6-10", value: "6-10" },
        { text: "10+", value: "10+" },
      ],
      filteredValue: tableFilters.availableDays || null,
      onFilter: (value: React.Key | boolean, item: Stock) => {
        if (value === "0") return item.availableDays === 0;
        if (value === "1-5")
          return item.availableDays >= 1 && item.availableDays <= 5;
        if (value === "6-10")
          return item.availableDays >= 6 && item.availableDays <= 10;
        if (value === "10+") return item.availableDays > 10;
        return true;
      },
      sorter: (a, b) => a.availableDays - b.availableDays,
      sortOrder:
        tableSorter.field === "availableDays" ? tableSorter.order : undefined,
    },
    {
      title: "Marriage days",
      dataIndex: "marriageDays",
      key: "marriageDays",
      width: 130,
      sorter: (a, b) => (a.marriageDays || 0) - (b.marriageDays || 0),
      sortOrder:
        tableSorter.field === "marriageDays" ? tableSorter.order : undefined,
      render: (_: unknown, item: Stock) => item.marriageDays || 0,
    },
    {
      title: "Child birth days",
      dataIndex: "childBirthDays",
      key: "childBirthDays",
      width: 130,
      sorter: (a, b) => (a.childBirthDays || 0) - (b.childBirthDays || 0),
      sortOrder:
        tableSorter.field === "childBirthDays" ? tableSorter.order : undefined,
      render: (_: unknown, item: Stock) => item.childBirthDays || 0,
    },
    {
      title: "Death of relative days",
      dataIndex: "deathOfRelativeDays",
      key: "deathOfRelativeDays",
      width: 150,
      sorter: (a, b) => (a.deathOfRelativeDays || 0) - (b.deathOfRelativeDays || 0),
      sortOrder:
        tableSorter.field === "deathOfRelativeDays" ? tableSorter.order : undefined,
      render: (_: unknown, item: Stock) => item.deathOfRelativeDays || 0,
    },
    {
      title: "Blood donation days",
      dataIndex: "bloodDonationDays",
      key: "bloodDonationDays",
      width: 140,
      sorter: (a, b) => (a.bloodDonationDays || 0) - (b.bloodDonationDays || 0),
      sortOrder:
        tableSorter.field === "bloodDonationDays" ? tableSorter.order : undefined,
      render: (_: unknown, item: Stock) => item.bloodDonationDays || 0,
    },
    {
      title: "Unpaid leave days",
      dataIndex: "unpaidLeaveDays",
      key: "unpaidLeaveDays",
      width: 130,
      sorter: (a, b) => (a.unpaidLeaveDays || 0) - (b.unpaidLeaveDays || 0),
      sortOrder:
        tableSorter.field === "unpaidLeaveDays" ? tableSorter.order : undefined,
      render: (_: unknown, item: Stock) => item.unpaidLeaveDays || 0,
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      fixed: "right",
      render: (_: unknown, stock: Stock) => (
        <Tooltip title="View chart">
          <Button
            icon={<PieChartOutlined />}
            onClick={() => setSelectedUser(stock.employeeId)}
          />
        </Tooltip>
      ),
    },
  ];

  const mobileColumns: ColumnsType<Stock> = [
    {
      title: "Employee name",
      dataIndex: "employeeName",
      key: "employeeName",
      filteredValue: tableFilters.employeeName || null,
      filterDropdown: ({
        selectedKeys,
        setSelectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search employee"
            value={selectedKeys[0] as string}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              size="small"
              onClick={() => confirm()}
            >
              Search
            </Button>
            <Button
              size="small"
              onClick={() => {
                clearFilters?.();
                confirm();
              }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
      onFilter: (value, record) =>
        record.employeeName
          ?.toLowerCase()
          .includes(String(value).toLowerCase()) || false,
      sorter: (a, b) => a.employeeName.localeCompare(b.employeeName),
      sortOrder:
        tableSorter.field === "employeeName" ? tableSorter.order : undefined,
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      render: (_: unknown, stock: Stock) => (
        <Tooltip title="View chart">
          <Button
            icon={<PieChartOutlined />}
            onClick={() => setSelectedUser(stock.employeeId)}
          />
        </Tooltip>
      ),
    },
  ];
  const displayedColumns = columns.filter(
    (column) =>
      !EXTRA_COLUMN_KEYS.has(column.key as string) ||
      visibleExtraColumns.includes(column.key as string),
  );
  return (
    <div className="stocks mt-5">
      <div className="flex items-center justify-between gap-4 mb-4">
        <Typography.Title level={4} className="!m-0">
          Team leave balance
        </Typography.Title>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Select
            mode="multiple"
            allowClear
            placeholder="Show extra columns"
            className="min-w-[220px]"
            options={EXTRA_COLUMN_OPTIONS}
            value={visibleExtraColumns}
            onChange={setVisibleExtraColumns}
            maxTagCount="responsive"
          />
          <Tooltip title="Download report">
            <Button
              icon={<DownloadOutlined />}
              onClick={handleReportDownload}
            />
          </Tooltip>
          {canRunRollover && (
            <Tooltip title="Run annual leave rollover">
              <Button
                icon={<SyncOutlined />}
                onClick={() => setRolloverModalOpen(true)}
              />
            </Tooltip>
          )}
        </div>
      </div>
      <div className="mb-2 flex items-center justify-start gap-2 flex-wrap">
        <EmployeeStatusFilter
          value={employeeStatusFilter}
          onChange={setEmployeeStatusFilter}
        />
        <DatePicker
          allowClear
          format="DD/MM/YYYY"
          value={selectedDate}
          onChange={(date) => setSelectedDate(date)}
          placeholder="Current date"
        />
      </div>
      <Modal
        title="Run annual leave rollover"
        open={rolloverModalOpen}
        onCancel={() => setRolloverModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Typography.Paragraph>
          This grants each active employee their configured Entitlement as
          new Current Year Days for the current calendar year and carries
          forward their unused days (from both Current Year Days and
          Previous Year Days, minus what was already used) into the new
          Previous Year Days. Employees with no Entitlement configured are
          skipped and reported by email.
        </Typography.Paragraph>
        <Typography.Paragraph>
          Choose what to do for employees who already have a balance
          calculated for this year:
        </Typography.Paragraph>
        <Space direction="vertical" className="w-full">
          <Button
            block
            loading={rolloverLoading}
            onClick={() => handleRunRollover(false)}
          >
            Skip employees who already have a balance for this year
          </Button>
          <Button
            block
            danger
            loading={rolloverLoading}
            onClick={() => handleRunRollover(true)}
          >
            Overwrite existing balances for this year
          </Button>
        </Space>
      </Modal>
      <Row>
        <Col xs={24} lg={16}>
          <div className="table-container">
            <Table
              scroll={{ x: true, y: fixedTableScrollY }}
              rowKey={(record) => `${record.employeeId}-${record.year}`}
              columns={isMobile ? mobileColumns : displayedColumns}
              dataSource={filteredSortedStocks}
              loading={loading}
              locale={{ emptyText: "No stock records found." }}
              onChange={(pagination, filters, sorterInput, extra) => {
                const sorter = Array.isArray(sorterInput)
                  ? sorterInput[0]
                  : (sorterInput as SorterResult<Stock>);
                const isFilterOrSortChange =
                  extra?.action === "filter" || extra?.action === "sort";
                setCurrentPage(
                  isFilterOrSortChange ? 1 : pagination.current || 1,
                );
                setPageSize(pagination.pageSize || rowsPerPage);
                setTableFilters(
                  filters as Record<string, (string | number)[] | null>,
                );
                if (sorter?.field && sorter?.order) {
                  setTableSorter({
                    field: String(sorter.field),
                    order: sorter.order,
                  });
                } else {
                  setTableSorter({ field: "employeeName", order: "ascend" });
                }
              }}
              pagination={{
                current: currentPage,
                total: filteredSortedStocks.length,
                pageSize,
                showSizeChanger: true,
                pageSizeOptions: ["5", "10", "20", "50"],
                showLessItems: true,
              }}
            />
          </div>
        </Col>
        <Col xs={24} lg={8} className="flex justify-center">
          {selectedUserData ? (
            <DonutChart
              title={`Leave balance - ${selectedUserData.employeeName}`}
              userData={selectedUserData}
            />
          ) : teamTotals ? (
            <DonutChart title="Team overview" userData={teamTotals} />
          ) : null}
        </Col>
      </Row>
    </div>
  );
};

export default StockRecord;
